import api from './api';

/**
 * Dashboard API Service
 * Serviço para comunicação com os endpoints do dashboard
 */

export interface SalesStats {
  total_sales: number;
  total_opportunities: number;
  won_deals: number;
  lost_deals: number;
  conversion_rate: number;
  average_ticket: number;
  average_deal_time: number;
  active_deals: number;
  win_rate: number;
  loss_rate: number;
  total_discounts: number;
  total_freight: number;
  revenue_by_attendant: {
    attendant_id: number;
    attendant_name: string;
    total_sales: number;
    won_deals: number;
    conversion_rate: number;
  }[];
}

export interface MonthlySales {
  month: number;
  month_name: string;
  total_sales: number;
  won_deals: number;
  lost_deals: number;
}

export interface StageConversion {
  stage_id: number;
  stage_name: string;
  opportunity_count: number;
  conversion_to_next: number;
  avg_time_in_stage: number;
}

export interface TopProduct {
  product_name: string;
  total_quantity: number;
  total_revenue: number;
  avg_price: number;
}

export interface AtRiskOpportunity {
  id: number;
  internal_code: string;
  lead_name: string;
  stage_name: string;
  total_value: number;
  last_activity: string;
  days_inactive: number;
}

/**
 * Get sales statistics
 * @param params - Query parameters (start_date, end_date, pipeline_id, attendant_id)
 */
export const getSalesStats = async (params?: {
  start_date?: string;
  end_date?: string;
  pipeline_id?: number;
  attendant_id?: number;
}): Promise<SalesStats> => {
  const response = await api.get<SalesStats>('/dashboard/sales_stats/', { params });
  return response.data;
};

/**
 * Get monthly sales data
 * @param params - Query parameters (year, pipeline_id)
 */
export const getMonthlySales = async (params?: {
  year?: number;
  pipeline_id?: number;
}): Promise<{ year: number; monthly_data: MonthlySales[] }> => {
  const response = await api.get<{ year: number; monthly_data: MonthlySales[] }>('/dashboard/monthly_sales/', {
    params,
  });
  return response.data;
};

/**
 * Get stage conversion rates
 * @param params - Query parameters (pipeline_id, start_date, end_date)
 */
export const getStageConversion = async (params: {
  pipeline_id: number;
  start_date?: string;
  end_date?: string;
}): Promise<{ stage_data: StageConversion[] }> => {
  const response = await api.get<{ stage_data: StageConversion[] }>('/dashboard/stage_conversion/', {
    params,
  });
  return response.data;
};

/**
 * Get top selling products
 * @param params - Query parameters (limit, start_date, end_date)
 */
export const getTopProducts = async (params?: {
  limit?: number;
  start_date?: string;
  end_date?: string;
}): Promise<{ products: TopProduct[] }> => {
  const response = await api.get<{ products: TopProduct[] }>('/dashboard/top_products/', {
    params,
  });
  return response.data;
};

/**
 * Get at-risk opportunities
 * @param params - Query parameters (pipeline_id, days_without_activity)
 */
export const getAtRiskOpportunities = async (params?: {
  pipeline_id?: number;
  days_without_activity?: number;
}): Promise<{ opportunities: AtRiskOpportunity[] }> => {
  const response = await api.get<{ opportunities: AtRiskOpportunity[] }>(
    '/dashboard/at_risk_opportunities/',
    { params }
  );
  return response.data;
};

/**
 * Export dashboard data to CSV
 * @param data - Data to export
 * @param filename - Name of the file to download
 */
export const exportToCSV = (data: any[], filename: string) => {
  const headers = Object.keys(data[0]).join(',');
  const rows = data.map((row) => Object.values(row).join(','));
  const csvContent = [headers, ...rows].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Format number to currency
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

/**
 * Format number to percentage
 */
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

/**
 * Format number with commas
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('pt-BR').format(value);
};
